import { Component } from '@angular/core';
import { BaseChartComponent, IChartReportModel } from './base-chart.component';
import { IChartReport, SECTION_CATEGORY_COUNT, SECTION_CUSTOMER_REVENUE, SECTION_DATE_REVENUE, SECTION_RESOURCES, SECTION_MOM_REVENUE } from './chart.service';
import { ResponsiveService } from '@app/shared/responsive/responsive.service';
import { AuthService } from '@app/shared/services/auth/auth.service';
import { GaStateService } from '@app/shared/services/ga-state.service';
import { GaFormlyService, IExtraFieldConfigurationObject } from '@app/shared/formly/ga-formly.service';
import { ReportFiltersUtil } from '@app/shared/report/report-filters-util.service';
import { ReportDataService } from '@app/shared/report/report-data.service';
import { StateService } from '@uirouter/angular';
import { ChartService } from './chart.service';
import { GaMessageService } from '@app/shared/message/ga-message.service';
import { TranslationService } from '@app/shared/translation/translation.service';
import { Override } from '@app/shared/app-decorators';
import { CHART_REPORT_ID } from './base-chart.component';
import { IUserData } from '@app/shared/services/auth/auth-data.service';
import _ from 'underscore';
import { IObject, IValueObject } from '@type_definitions/datatypes';
import { FormattingService } from '@app/shared/services/formatting.service';
import { OdDataService } from '../dashboard/filters/od/od-data.service';
import { ReportGridService } from '@app/shared/report/report-grid.service';
import { CurrencyService } from '@app/shared/services/currency.service';
import { CompanyType } from '@app/shared/company/company-data.service';

@Component({
  selector: 'chart-bp',
  templateUrl: './base-chart.component.html'
})
export class ChartBpComponent extends BaseChartComponent {
  private resourcesSection:IChartReport;
  private dateReport:IChartReport;
  private customerReport:IChartReport;
  private categoryReport:IChartReport;
  private momRevenueReport:IChartReport;
  private bpSelectField:IExtraFieldConfigurationObject;
  private bpGroupField:IExtraFieldConfigurationObject;
  private defaultBP:string;

  constructor(
    responsiveService:ResponsiveService,
    authService:AuthService,
    gaStateService:GaStateService,
    formlyService:GaFormlyService,
    reportFiltersUtil:ReportFiltersUtil,
    reportDataService:ReportDataService,
    stateService:StateService,
    chartService:ChartService,
    reportGridService:ReportGridService,
    currencyService:CurrencyService,
    formattingService:FormattingService,
    messageService:GaMessageService,
    private translationService:TranslationService,
    private odDataService:OdDataService
  ) {
    super(responsiveService, authService, gaStateService, formlyService, reportFiltersUtil, reportDataService, stateService, chartService, reportGridService, currencyService, formattingService, messageService);
  }

  @Override(BaseChartComponent)
  public chartOnInit():void {
    const currentUser:IUserData = this.authService.getCurrentUser();
    const isBPUser:boolean = currentUser.location?.companyCountry?.company?.companyType === CompanyType.BP;
    const userCompanyCountryId:number = currentUser.location?.companyCountry?.id;

    this.defaultBP = isBPUser? userCompanyCountryId.toString() : undefined;
    this.model = this.paramsToModel(this.stateService.params);

    this.resourcesSection = {
      id: SECTION_RESOURCES,
      title: this.getSectionTitle(SECTION_RESOURCES),
      collapsed: true,
      listFaqs: ['BUSINESS_PRACTICE_GUIDELINES'],
      params: undefined
    };

    this.dateReport = {
      id: SECTION_DATE_REVENUE,
      title: this.getSectionTitle(SECTION_DATE_REVENUE),
      collapsed: true,
      params: this.createReportParams()
    };

    this.customerReport = {
      id: SECTION_CUSTOMER_REVENUE,
      title: this.getSectionTitle(SECTION_CUSTOMER_REVENUE),
      collapsed: true,
      params: this.createReportParams()
    };

    this.categoryReport = {
      id: SECTION_CATEGORY_COUNT,
      title: this.getSectionTitle(SECTION_CATEGORY_COUNT),
      collapsed: true,
      params: this.createReportParams()
    };
    
    this.momRevenueReport = {
      id: SECTION_MOM_REVENUE,
      title: this.getSectionTitle(SECTION_MOM_REVENUE),
      collapsed: false,
      params: this.createReportParams()
    };

    this.chartReports = [
      this.resourcesSection,
      this.momRevenueReport,
      this.dateReport,
      this.customerReport,
      this.categoryReport
    ];

    this.fields = this.createFields();
  }

  @Override(BaseChartComponent)
  public createReports():void {
    this.dateReport = _.extend(this.dateReport, this.createDateReport());
    this.customerReport = _.extend(this.customerReport, this.createCustomerReport());
    this.categoryReport = _.extend(this.categoryReport, this.createCategoryReport());
    this.momRevenueReport = _.extend(this.momRevenueReport, this.createMoMRevenueReport());
  }

  @Override(BaseChartComponent)
  public validateFilters(doNotShowValidationErrors?:boolean):boolean {
    if (!this.model.bp && !this.model.bpGroup) {
      if (!doNotShowValidationErrors) {
        this.messageService.showErrorDialog(this.translationService.instant('GA.VALIDATION.VALIDATION_FAILED'), this.translationService.instant('CHART.VALIDATION.REQUIRED_BP'));
      }

      return false;
    }

    return true;
  }

  @Override(BaseChartComponent)
  public paramsEqual(p1:IObject, p2:IObject):boolean {
    const paramNames:string[] = [
      'from',
      'to',
      'customer',
      'currency',
      'webshop',
      'bp',
      'bpGroup'
    ];

    for (const paramName of paramNames) {
      if (((p1 && p1[ paramName ]) || undefined) !== ((p2 && p2[ paramName ]) || undefined)) {
        return false;
      }
    }

    return true;
  }

  @Override(BaseChartComponent)
  public modelToParams():IObject {
    const from:string = _.isDate(this.model.orderDate_FROM)? this.formattingService.formatDate(this.model.orderDate_FROM) : this.model.orderDate_FROM;
    const to:string = _.isDate(this.model.orderDate_TO)? this.formattingService.formatDate(this.model.orderDate_TO) : this.model.orderDate_TO;

    return {
      bp: this.model.bp?.toString(), // split array into comma separated
      currency: this.model.currency !== this.defaultCurrency? this.model.currency : undefined,
      from: from !== this.defaultFromDate? from : this.defaultFromDate,
      to: to,
      bpGroup: this.model.bpGroup && this.model.bpGroup.toString() // because $stateParams always string
    };
  }

  protected getReportFilters():IObject {
    return _.extend(super.getReportFilters(), {Price_type:2});
  }

  @Override(BaseChartComponent)
  public paramsToModel(params:any):IChartReportModel {
    const from:Date = this.chartService.getDateRangeValue(params.from);
    const to:Date = this.chartService.getDateRangeValue(params.to);

    return {
      bp: params.bp?.split(','), // transform comma separated to array
      currency: params.currency || this.defaultCurrency,
      orderDate_FROM: from || (this.form?.touched ? null : this.defaultFromDate),
      orderDate_TO: to,
      bpGroup: params.bpGroup && parseInt(params.bpGroup, 10)
    };
  }

  @Override(BaseChartComponent)
  public fillFields():void {
    if (this.bpSelectField) {
      // find customer column id
      const bpColumnId:string = _.findWhere(this.reportMetaData.columns, { name: 'Bp' }).id;
      // load list of customers
      this.formlyService.fillField(this.bpSelectField, this.reportDataService.loadListFilterOptions(CHART_REPORT_ID, bpColumnId))
        .then((bps:IValueObject[]) => {
          // if bp response only one then pre-select and start load
          if (bps?.length === 1) {
            this.model.bp = [ <string>bps[ 0 ].id ];
            this.startLoad();
          }
        });
    }

    this.formlyService.fillField(this.bpGroupField, this.odDataService.BPGroups());
  }

  private createFields():IExtraFieldConfigurationObject[] {
    return [
      this.bpSelectField = this.formlyService.aMultiSelect('bp', 'BP')
        .withoutEditable()
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .withLabelProp('value')
        .withValueProp('id')
        .onChange(this.onModelChange)
        .withExtraOptions({
          templateOptions: {
            localSearch: true
          }
        })
        .build(),
      this.bpGroupField = this.formlyService.aSelect('bpGroup', 'BP Group')
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .withLabelProp('name')
        .withValueProp('id')
        .onChange((bpGroup:number) => {
          // clear bp selection when bp group selected and return default bp when bp group is cleared
          this.model.bp = !bpGroup && this.defaultBP? [ this.defaultBP ] : undefined;

          // trigger onModelChange
          this.onModelChange();
        })
        .build(),
      this.createCurrencyField(),
      this.createOrderDateFields()
    ];
  }
}